import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from './cn.js';
/**
 * Heights read the density tokens: `h-control` is 36 px comfortable / 32 px
 * compact, `h-control-sm` four pixels less. `size` is the *variant* vocabulary
 * (a small button in a table row, an icon-only button), never a density knob.
 */
const buttonVariants = cva('inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-ui font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0', {
    variants: {
        variant: {
            default: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
            destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
            outline: 'border border-input bg-background shadow-xs hover:border-input-hover hover:bg-accent hover:text-accent-foreground',
            secondary: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
            ghost: 'hover:bg-accent hover:text-accent-foreground',
            link: 'text-primary-text underline-offset-4 hover:underline',
            success: 'bg-success text-success-foreground shadow-sm hover:bg-success/90',
        },
        size: {
            default: 'h-control px-4 py-2',
            sm: 'h-control-sm rounded-md px-3 text-body',
            lg: 'h-10 rounded-md px-6',
            icon: 'size-control',
            'icon-sm': 'size-control-sm',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});
const Button = React.forwardRef(({ className, variant, size, asChild = false, loading = false, disabled, type = 'button', children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (_jsx(Comp, { ref: ref, type: asChild ? undefined : type, "data-slot": "button", "aria-busy": loading || undefined, disabled: disabled || loading, className: cn(buttonVariants({ variant, size }), loading && 'relative', className), ...props, children: loading ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "absolute inset-0 flex items-center justify-center", "aria-hidden": "true", children: _jsx(Loader2, { className: "animate-spin" }) }), _jsx("span", { className: "invisible inline-flex items-center gap-2", children: children })] })) : (children) }));
});
Button.displayName = 'Button';
export { Button, buttonVariants };
//# sourceMappingURL=button.js.map